import { useEffect, useState } from "react";
import { Loader2, Sparkles, FlaskConical, Beaker } from "lucide-react";
import { tr } from "@/utils/i18n";

/**
 * Full-screen overlay shown during AI cycle generation.
 * Surfaces an elapsed-time counter + rotating status messages so users
 * don't think the app is frozen.
 */
export function GenerationOverlay({ visible, language = "en" }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!visible) {
      setElapsed(0);
      return undefined;
    }
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [visible]);

  if (!visible) return null;

  const stages = [
    {
      Icon: FlaskConical,
      title: tr(language, "Analyzing your goals", "Analisando seus objetivos", "Analizando tus objetivos"),
      sub: tr(language, "Matching peptides to your profile…", "Combinando peptídeos com seu perfil…", "Asociando péptidos a tu perfil…"),
    },
    {
      Icon: Beaker,
      title: tr(language, "Calculating dosages", "Calculando dosagens", "Calculando dosis"),
      sub: tr(language, "Optimizing for weight & experience…", "Otimizando para peso e experiência…", "Optimizando por peso y experiencia…"),
    },
    {
      Icon: Sparkles,
      title: tr(language, "Building your protocol", "Montando seu protocolo", "Construyendo tu protocolo"),
      sub: tr(language, "Almost ready — assembling weekly schedule…", "Quase pronto — montando a agenda semanal…", "Casi listo — armando el plan semanal…"),
    },
  ];

  // Rotate through stages every 15s; cap at 3rd stage
  const stageIdx = Math.min(Math.floor(elapsed / 15), stages.length - 1);
  const stage = stages[stageIdx];
  const Icon = stage.Icon;

  const mm = String(Math.floor(elapsed / 60)).padStart(1, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
      role="dialog"
      aria-live="polite"
      data-testid="generation-overlay"
    >
      <div className="bg-gradient-to-br from-zinc-900 to-black border border-cyan-500/30 rounded-2xl p-8 max-w-md w-full shadow-2xl shadow-cyan-500/10">
        {/* Animated icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-cyan-500/20 blur-2xl animate-pulse" />
            <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500/30 to-violet-500/30 border border-cyan-500/40 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" />
              <Icon className="absolute w-7 h-7 text-cyan-300" />
            </div>
          </div>
        </div>

        {/* Timer */}
        <div className="text-center mb-6">
          <div
            className="font-mono text-5xl font-bold text-cyan-400 tracking-wider tabular-nums"
            data-testid="generation-timer"
          >
            {mm}:{ss}
          </div>
          <p className="text-xs text-zinc-500 mt-1 uppercase tracking-widest">
            {tr(language, "Time elapsed", "Tempo decorrido", "Tiempo transcurrido")}
          </p>
        </div>

        {/* Stage info */}
        <div className="text-center">
          <h3 className="text-xl font-semibold text-white mb-1">{stage.title}</h3>
          <p className="text-sm text-zinc-400">{stage.sub}</p>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 mt-6">
          {stages.map((s, i) => (
            <div
              key={s.title}
              className={`h-1.5 rounded-full transition-all ${
                i <= stageIdx ? "bg-cyan-400 w-8" : "bg-zinc-700 w-2"
              }`}
            />
          ))}
        </div>

        <p className="text-center text-xs text-zinc-600 mt-6">
          {tr(language,
            "AI generation typically takes 30–60 seconds. Please don't refresh.",
            "A geração por IA normalmente leva 30–60 segundos. Por favor, não atualize.",
            "La generación con IA suele tardar 30–60 segundos. No actualices, por favor."
          )}
        </p>
      </div>
    </div>
  );
}
